import { APP_CAN_APPS_V3 } from 'constants/appNames';
import type { SearchResult } from './types';

export interface NavigationAction {
  id: string;
  title: string;
  path: string;
  appName: string;
  isInternal: boolean;
}

interface SidebarTab {
  id?: string;
  name: string;
  url: string;
  type?: string;
  subtabs?: SidebarTab[];
}

interface SidebarMenu {
  id: number;
  name: string;
  tabs?: SidebarTab[];
}

/**
 * Build navigation actions from the RBAC-filtered sidebar menus.
 * Only includes routes the current user has access to.
 */
export function buildActionsFromMenus(menus: SidebarMenu[]): NavigationAction[] {
  const actions: NavigationAction[] = [];

  for (const menu of menus) {
    if (!menu.tabs) continue;

    for (const tab of menu.tabs) {
      // Skip sub-menu containers (they have subtabs instead)
      if (tab.url && tab.url !== 'Sub-menu') {
        const isInternal = tab.type === APP_CAN_APPS_V3;
        actions.push({
          id: `nav-${menu.name}-${tab.name}`.replace(/\s+/g, '-').toLowerCase(),
          title: `Go to ${tab.name}`,
          path: isInternal ? tab.url : tab.url,
          appName: menu.name,
          isInternal,
        });
      }

      // Process subtabs
      if (tab.subtabs) {
        for (const subtab of tab.subtabs) {
          if (!subtab.url || subtab.url === 'Sub-menu') continue;
          const isInternal = subtab.type === APP_CAN_APPS_V3;
          actions.push({
            id: `nav-${menu.name}-${tab.name}-${subtab.name}`.replace(/\s+/g, '-').toLowerCase(),
            title: `Go to ${subtab.name}`,
            path: isInternal ? subtab.url : subtab.url,
            appName: menu.name,
            isInternal,
          });
        }
      }
    }
  }

  return actions;
}

/**
 * Filter navigation actions by query string and convert to SearchResults.
 * Matches against title and app name (case-insensitive).
 * Only navigates internal (SPA) routes; external links open in same tab.
 */
export function filterActions(
  actions: NavigationAction[],
  query: string,
  navigate: (path: string) => void,
): SearchResult[] {
  const toResult = (action: NavigationAction): SearchResult => ({
    id: action.id,
    category: 'navigation' as const,
    title: action.title,
    subtitle: `${action.appName} - ${action.path}`,
    onSelect: () => {
      if (action.isInternal) {
        navigate(action.path);
      } else {
        window.location.href = action.path;
      }
    },
  });

  if (!query) {
    return actions.map(toResult);
  }

  const lower = query.toLowerCase();
  return actions
    .filter(
      (action) =>
        action.title.toLowerCase().includes(lower) ||
        action.appName.toLowerCase().includes(lower) ||
        action.path.toLowerCase().includes(lower),
    )
    .map(toResult);
}
